<template>
  <div class="edit-adventure w-full flex flex-col items-center pb-72">
    <div v-if="!adventureRejected" class="flex flex-col w-full md:w-448 m-auto">
      <Popup v-model="showPopup" popupHeader="Unsaved changes" customClasses="overflow-visible">
        <div class="relative">
          <p class="text-body mb-16">If you leave your changes will be discarded</p>
          <Button
            value="Discard"
            size="medium"
            class="w-full"
            @click.prevent="
              () => {
                $store.dispatch('adventures/resetStore');
                $router.push({ path: viewAdventureRoute });
              }
            "
          />
          <Button
            value="Save"
            size="medium"
            variant="outlined"
            class="w-full mt-16"
            :loading="loadingPopup"
            @click.prevent="
              () => {
                loadingPopup = true;
                submitAdventure();
              }
            "
          />
        </div>
      </Popup>
      <div><Breadcrumb header="Cancel" :isButton="true" class="ml-16 md:ml-0" @click="cancelHandler" /></div>
      <h1 class="text-l font-medium mt-24 ml-16 md:ml-0">Edit {{ adventure.title }}</h1>
      <h2 class="text-s font-light text-grey-text-subdued m-16 md:ml-0" style="font-kerning: none">
        Make changes to the sections of your Adventure, then press Done to submit changes for review.
      </h2>
      <Card class="z-[1] mt-16 p-0">
        <li v-for="route in routesList" :key="route.path" class="block">
          <router-link
            :to="`/adventures/${adventureId}/edit/${route.path}`"
            class="border-b-1 border-solid border-grey-divider-subdued flex items-center justify-between px-16 py-8 h-64 w-full text-left primary-focus"
          >
            <div>
              <div class="text-s font-medium">{{ route.title }}</div>
              <div
                class="block text-s font-light mt-4 max-w-320 text-ellipsis overflow-hidden break-words"
                style="display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical"
              >
                {{ route.subtitle }}
              </div>
            </div>
            <DisclosureIcon :width="16" :height="16" />
          </router-link>
        </li>
      </Card>
      <StickyButton value="Done" variant="alwaysSticky" :loading="loadingDone" @click="doneHandler" />
    </div>
    <ReviewRejected v-else-if="adventureRejected" />
    <ReviewError v-if="adventureError" :adventureStatus="adventureStatus" @click="retrySubmit" @close="resetAdventureStatus" />
  </div>
</template>

<script lang="ts">
import pickBy from 'lodash/pickBy';
import Breadcrumb from '@/components/breadcrumb/Breadcrumb.vue';
import Card from '@/components/card/Card.vue';
import DisclosureIcon from '@/graphics/icons/DisclosureIcon.vue';
import StickyButton from '@/components/sticky_button/StickyButton.vue';
import Button from '@/components/button/Button.vue';
import Popup from '@/components/popup/Popup.vue';
import { UPDATE_ADVENTURE } from '@/graphql/adventures/mutations';
import ReviewRejected from '../create/review/ReviewRejected.vue';
import ReviewError from '../create/review/ReviewError.vue';

import { ADVENTURE_DETAILS_QUERY } from '@/graphql/adventures/queries';

export default {
  name: 'Edit',
  components: { Breadcrumb, Card, DisclosureIcon, StickyButton, Popup, Button, ReviewRejected, ReviewError },
  data() {
    return {
      adventureStatus: '',
      loadingDone: false,
      loadingPopup: false,
      showPopup: false,
    };
  },
  computed: {
    adventure(): any {
      return this.$store.getters['adventures/adventureDraft'];
    },
    adventureId(): number {
      return Number(this.$route?.params?.adventureId);
    },
    viewAdventureRoute(): string {
      return `/adventures/details/${this.adventureId}`;
    },
    adventureRejected(): boolean {
      return this.adventureStatus === 'rejected';
    },
    adventureError(): boolean {
      return this.adventureStatus === 'error';
    },
    routesList(): any {
      return [
        {
          title: 'Category',
          subtitle: this.adventure?.adventureCategoryName,
          path: 'category',
        },
        {
          title: 'Cover Photo',
          path: 'photo',
        },
        {
          title: 'Title',
          subtitle: this.adventure?.title,
          path: 'title',
        },
        {
          title: 'Surcharge',
          subtitle: `$${Math.floor(this.adventure?.surcharge / 100)}/child`,
          path: 'rate',
        },
        {
          title: 'Age Range',
          subtitle: this.adventure?.ageRangeText,
          path: 'age_range',
        },
        {
          title: 'Duration',
          subtitle: `${Math.floor(this.adventure?.duration / 60)} hours`,
          path: 'duration',
        },
        {
          title: 'What you will do',
          subtitle: this.adventure?.description,
          path: 'description',
        },
        {
          title: 'Why are you passionate',
          subtitle: this.adventure?.sitterPassion,
          path: 'passion',
        },
        {
          title: 'Supplies',
          subtitle: this.suppliesSubtitle,
          path: 'supplies',
        },
        {
          title: 'Availability',
          subtitle: this.availabilitySubtitle,
          path: 'availability/edit',
        },
      ];
    },
    suppliesSubtitle(): string {
      return [
        ...this.$store.getters['adventures/sitterSuppliesList'],
        ...this.$store.getters['adventures/parentSuppliesList'],
      ].join(', ');
    },
    availabilitySubtitle(): string {
      const output: string[] = [];
      const availability = this.adventure?.sitterAvailability || {};

      Object.keys(availability || [])?.forEach((day) => {
        if (availability[day]?.length && Array.isArray(availability[day])) {
          output.push(day[0].toUpperCase() + day.substring(1, 3));
        }
      });

      return output.join(', ');
    },
  },
  created() {
    const cache = JSON.parse(`${sessionStorage.getItem('adventureStore')}`);

    if (cache && !this.$store.getters['adventures/adventureDraft'].id) {
      this.$store.dispatch('adventures/updateStore', cache);
    } else {
      sessionStorage.setItem('adventureStore', JSON.stringify(this.$store.getters['adventures/adventureDraft']));
    }

    this.$store.dispatch('adventures/updatePending', {});
  },
  methods: {
    doneHandler() {
      if (this.$store.getters['adventures/adventureDraft'].edited) {
        this.loadingDone = true;
        this.submitAdventure();
      } else {
        this.$router.push({ path: this.viewAdventureRoute });
      }
    },
    cancelHandler() {
      if (this.$store.getters['adventures/adventureDraft'].edited) {
        this.showPopup = true;
      } else {
        this.$router.push({ path: this.viewAdventureRoute });
      }
    },
    resetAdventureStatus() {
      this.adventureStatus = '';
    },
    retrySubmit() {
      this.resetAdventureStatus();
      this.submitAdventure();
    },
    submitAdventure() {
      this.$apollo
        .mutate({
          mutation: UPDATE_ADVENTURE,
          variables: {
            input: this.$store.getters['adventures/apolloMutationInput'],
            updateAdventureId: this.adventureId,
          },
        })
        .then((payload) => {
          this.loadingDone = false;
          this.loadingPopup = false;
          const receivedStatus = payload.data.updateAdventure.adventure.status;
          if (receivedStatus === 'active') {
            this.$router.push({ path: this.viewAdventureRoute });
          } else if (receivedStatus === 'rejected') {
            this.adventureStatus = 'rejected';
            this.$store.dispatch('adventures/resetStore');
          } else {
            this.adventureStatus = 'error';
          }
        })
        .catch((error) => {
          this.loadingDone = false;
          this.loadingPopup = false;
          this.adventureStatus = 'error';
          console.error(error); // eslint-disable-line no-console
        });
      sessionStorage.removeItem('adventureStore');
    },
  },
  apollo: {
    adventureDetails: {
      query: ADVENTURE_DETAILS_QUERY,
      variables(): any {
        return {
          id: this.adventureId,
        };
      },
      fetchPolicy: 'network-only',
      result(response) {
        if (this.$store.getters['adventures/adventureId'] === this.adventureId) return;

        const adventureData = response.data.adventureDetails.adventure;
        const sitterAvailability = pickBy(adventureData.sitterAvailability, (value) => Array.isArray(value));
        const ageRangeIds = adventureData.ageRanges.map((ageRange) => ageRange.id);

        const adventure = {
          ...adventureData,
          sitterAvailability,
          ageRangeIds,
          edited: false,
        };

        this.$store.dispatch('adventures/updateStore', adventure);
      },
    },
  },
};
</script>
